import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useGetCmsConfigsQuery } from '../../apis/cmsApi/cms.hooks.api';
import { isPdrOrder } from '../../helpers/mlkActions';
import {
  getCorrectStatus,
  getExpectedServiceDateAndTimeSlot,
} from '../../helpers/order';
import {
  Order,
  OrderState,
  BlockedReason,
  CalculatedOrderStatus,
} from '../../models';
import { useOrder } from '../useOrder';

export const useIsAppointmentEnabled = () => {
  const order = useOrder();

  const { data: cmsConfigs } = useGetCmsConfigsQuery(undefined);

  const isAppointmentAvailable = useMemo(() => {
    if (order && cmsConfigs) {
      const calculatedOrderStatus = getCorrectStatus(order);
      return getIsAppointmentEnabled(
        order,
        calculatedOrderStatus,
        cmsConfigs!.featureFlags.isAppointmentEnabled
      );
    }
    return null;
  }, [cmsConfigs, order]);

  return isAppointmentAvailable;
};

const getIsAppointmentEnabled = (
  order: Order,
  calculatedOrderStatus: CalculatedOrderStatus | undefined,
  isServiceEnabled: boolean
) => {
  const isLastAttempt = order.blockedReasons?.includes(BlockedReason.B01);
  const isSuspended = calculatedOrderStatus === CalculatedOrderStatus.Suspended;

  const isPdr = isPdrOrder(order);
  const expectedDateAndTimeSlot = getExpectedServiceDateAndTimeSlot(order);
  let isSameDayPdr = false;
  if (expectedDateAndTimeSlot) {
    const today = dayjs();
    const hoursDifference = expectedDateAndTimeSlot.to
      .hour(0)
      .minute(0)
      .second(0)
      .diff(today, 'hour');
    isSameDayPdr = hoursDifference <= 0 && !!isPdr;
  }
  const isRescheduleEnabled =
    order.shipperTerritoryConfiguration.uiParams.enableReschedule;
  const isValidOrderState =
    order.status === OrderState.InTransit ||
    order.status === OrderState.ToReschedule ||
    (order.status === OrderState.Blocked &&
      order.blockedReasons?.includes(BlockedReason.B02));

  const isValidCalculatedStatus =
    calculatedOrderStatus !== undefined &&
    (calculatedOrderStatus === CalculatedOrderStatus.Masked ||
      calculatedOrderStatus === CalculatedOrderStatus.StandBy ||
      calculatedOrderStatus === CalculatedOrderStatus.Scheduled ||
      calculatedOrderStatus === CalculatedOrderStatus.ScheduledReception);

  return (
    !isLastAttempt &&
    isRescheduleEnabled &&
    !isSuspended &&
    (isValidOrderState || isValidCalculatedStatus) &&
    !isSameDayPdr &&
    isServiceEnabled
  );
};
